import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCompass,
  FiFeather,
  FiHeart,
  FiLayers,
  FiSmile,
  FiPenTool,
} from "react-icons/fi";
import routes from "../routes";

const About = () => {
  const { t } = useTranslation();
  const [activeCollectionSlide, setActiveCollectionSlide] = useState(0);
  const [isCollectionSliderPaused, setIsCollectionSliderPaused] =
    useState(false);

  const highlights = t("aboutPage.highlights", { returnObjects: true });
  const values = t("aboutPage.values", { returnObjects: true });
  const stats = t("aboutPage.stats", { returnObjects: true });
  const marketplaceLogos = [
    {
      name: "Amazon",
      logo: "/images/amazon.png",
      url: "https://www.amazon.com.tr/stores/CrossBorder/page/26E68055-F625-4167-A0BA-1037467FDA29?ref_=cm_sw_r_ud_ast_store_XXSD2VHSCKEGQADWAC1M",
    },
    {
      name: "Amazon Japan",
      logo: "/images/amazonJP.png",
      url: "https://www.amazon.co.jp/CROSS-BORDER-%E3%83%AA%E3%83%A9%E3%83%83%E3%82%AF%E3%82%B9%E7%94%A8%EF%BD%9C%E3%83%A9%E3%83%99%E3%83%B3%E3%83%80%E3%83%BC%E3%81%AE%E9%A6%99%E3%82%8A-%E3%83%9B%E3%83%83%E3%83%88%EF%BC%86%E3%82%B3%E3%83%BC%E3%83%AB%E3%83%89%E3%83%91%E3%83%83%E3%82%AF%EF%BD%9C%E9%A6%96%E3%83%BB%E8%82%A9%E3%83%BB%E8%85%B0%E3%81%BE%E3%82%8F%E3%82%8A%E7%94%A8%EF%BD%9C100-%E3%82%B3%E3%83%83%E3%83%88%E3%83%B3%EF%BD%9C%E6%97%A5%E6%9C%AC%E8%A3%BD-%E3%83%8F%E3%83%B3%E3%83%89%E3%83%A1%E3%82%A4%E3%83%89%EF%BC%8850%C3%9715cm%EF%BC%89/dp/B0GJM8FLLX/ref=sr_1_6?dib=eyJ2IjoiMSJ9.mP9tn5y3PMZgsKex3v7J-Jpf0f9uOfDCTT30vfGer3qJOov6tnbtwq9nQvnXG60cAl5C3-y59wRSuiZHvFe2H4-_RUf26n1M2-KJUlQGdZlzmtI_0F2nutgAX_lwmi-czG0aOaPEV7Erbwg_PFj1nnCHpxhZWfkc1nDde2ydKHJbJBLA7s0vJCWLMOHRDMsh3dHopplFdHOraE0aZ9qjrFVgVz7wA6ggu1ImHRg9GbeEZGP1n-FS9dzbOK3X8Lk7MJ57WJ7rUS0bYxjckZRr8xWNQ-ygMfTi0Ixbd4lzaQM.wKTPD0oN1AzGhua_t5_J6fgOyyTr4zaECEzNbBcYW0M&dib_tag=se&keywords=CROSS%2BBORDER&qid=1776181530&sr=8-6&th=1",
    },
    {
      name: "Trendyol",
      logo: "/images/trendyol.png",
      url: "https://ty.gl/1ifct8gwqgirr",
    },
    {
      name: "Hepsiburada",
      logo: "/images/hepsiburada.png",
      url: "https://www.hepsiburada.com/magaza/crossborder-store",
    },
    {
      name: "CicekSepeti",
      logo: "/images/cicek.png",
      url: "https://www.ciceksepeti.com/your-shop-link",
    },
  ];

  const collectionSlides = [
    {
      image: "/images/wallarts.webp",
      title: t("aboutSection.block2.title"),
      description: t("aboutSection.block2.paragraph"),
    },
    {
      image: "/images/aroma2.webp",
      title: t("aboutSection.block3.title"),
      description: t("aboutSection.block3.paragraph"),
    },
    {
      image: "/images/gift.webp",
      title: t("aboutSection.block4.title"),
      description: t("aboutSection.block4.paragraph"),
    },
    {
      image: "/images/dates.webp",
      title: t("aboutSection.block5.title"),
      description: t("aboutSection.block5.paragraph"),
    },
  ];

  useEffect(() => {
    if (isCollectionSliderPaused || collectionSlides.length <= 1) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setActiveCollectionSlide(
        (prevSlide) => (prevSlide + 1) % collectionSlides.length,
      );
    }, 6000);

    return () => clearInterval(intervalId);
  }, [collectionSlides.length, isCollectionSliderPaused]);

  const highlightIcons = [FiCompass, FiHeart, FiLayers];
  const valueIcons = [FiFeather, FiSmile, FiHeart, FiPenTool];

  return (
    <div className="min-h-screen bg-[#f7f0ec] pt-20 font-raleway text-[#5f2f42]">
      <section
        className="relative overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgb(37 16 25 / 76%), rgba(167, 103, 94, 0.52)), url(/images/nz1.webp)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-6 py-20 md:px-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] lg:items-center lg:px-12 lg:py-28">
          <div className="max-w-3xl text-center lg:text-left">
            <p className="mb-4 text-sm uppercase tracking-[0.32em] text-[#f3d8cf]">
              Cross Border
            </p>
            <h1 className="max-w-2xl text-4xl font-semibold leading-tight text-white md:text-5xl lg:text-6xl">
              {t("aboutPage.hero.title")}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-white/85 md:text-lg">
              {t("aboutPage.hero.description")}
            </p>
            <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-4">
              <Link
                to={routes.products}
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold tracking-[0.14em] text-[#7e3957] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#fff7f3]"
              >
                {t("aboutPage.hero.primaryCta")}
                <FiArrowRight />
              </Link>
              <Link
                to={routes.contactUs}
                className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-6 py-3 text-sm font-semibold tracking-[0.14em] text-white backdrop-blur-sm hover:-translate-y-0.5 transition-all duration-300 hover:bg-white/18"
              >
                {t("aboutPage.hero.secondaryCta")}
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -left-6 top-10 hidden h-24 w-24 rounded-full bg-[#f1d2c4]/40 blur-2xl md:block" />
            <div className="absolute -bottom-8 right-6 hidden h-28 w-28 rounded-full bg-[#f9ede4]/40 blur-2xl md:block" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/20 bg-white/14 p-5 shadow-[0_28px_80px_-32px_rgba(32,13,21,0.6)] backdrop-blur-md">
              <div className="grid gap-4 sm:grid-cols-2">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-[1.4rem] border border-white/18 bg-white/14 p-5 text-white text-center sm:text-left transition-all duration-300 hover:bg-white/20 hover:border-white/40"
                  >
                    <p className="text-3xl font-semibold md:text-4xl">
                      {stat.value}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-white/75">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-4 overflow-hidden rounded-[1.6rem] bg-[linear-gradient(145deg,rgba(255,255,255,0.28),rgba(255,255,255,0.1))] p-4">
                <img
                  src="/images/wheatbag/machine.webp"
                  alt={t("aboutPage.hero.imageAlt")}
                  className="h-[280px] w-full rounded-[1.2rem] object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="relative mx-auto max-w-6xl">
          <div className="max-w-2xl text-center md:text-left mx-auto md:mx-0">
            <p className="text-sm font-semibold uppercase tracking-[0.26em] text-[#b17075]">
              {t("aboutPage.highlightsEyebrow")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold text-[#83375B] md:text-4xl">
              {t("aboutPage.highlightsTitle")}
            </h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {highlights.map((item, index) => {
              const Icon = highlightIcons[index] || FiCompass;

              return (
                <article
                  key={item.title}
                  className="group rounded-[1.75rem] border border-[#e8d8d1] bg-white/85 p-7 text-center md:text-left shadow-[0_22px_55px_-35px_rgba(88,43,62,0.45)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_70px_-32px_rgba(88,43,62,0.55)]"
                >
                  <div className="mx-auto md:mx-0 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f6e4db] text-2xl text-[#83375B] transition-transform duration-300 group-hover:scale-110">
                    <Icon />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-[#83375B]">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-[#6f5a63]">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 pb-14 sm:px-6 lg:px-8 lg:pb-16">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-[2rem] border border-[#eeded7] bg-[linear-gradient(160deg,#fff8f4,#f6ece7)] px-6 py-8 shadow-[0_24px_60px_-42px_rgba(76,36,56,0.45)] md:px-10 md:py-10">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.26em] text-[#b17075]">
              {t("aboutPage.marketplace.eyebrow")}
            </p>
            <h2 className="mt-3 text-2xl font-semibold text-[#83375B] md:text-3xl">
              {t("aboutPage.marketplace.title")}
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-7 text-[#6f5a63] md:text-base">
              {t("aboutPage.marketplace.description")}
            </p>
          </div>

          <div className="mt-7 flex flex-wrap justify-center gap-3">
            {marketplaceLogos.map((marketplace) => (
              <a
                key={marketplace.name}
                href={marketplace.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-20 w-[calc(50%-6px)] items-center justify-center rounded-2xl border border-[#ead9d2] bg-white/90 px-4 shadow-[0_18px_35px_-30px_rgba(88,43,62,0.45)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_45px_-28px_rgba(88,43,62,0.5)] sm:w-[calc(20%-10px)]"
              >
                <img
                  src={marketplace.logo}
                  alt={marketplace.name}
                  className="max-h-[6rem] w-auto object-contain"
                />
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-14 sm:px-6 lg:px-8 lg:pb-16">
        <div className="mx-auto grid max-w-6xl gap-6 overflow-hidden rounded-[2rem] border border-[#ead8d2] bg-[linear-gradient(145deg,#fffaf7,#f3e7e1)] p-6 shadow-[0_24px_60px_-42px_rgba(76,36,56,0.45)] md:grid-cols-[minmax(0,1.2fr)_minmax(260px,0.8fr)] md:p-8">
          <div className="text-center md:text-left">
            <p className="text-sm font-semibold uppercase tracking-[0.26em] text-[#b17075]">
              {t("aboutPage.crossBorderPresence.eyebrow")}
            </p>
            <h2 className="mt-3 text-2xl font-semibold text-[#83375B] md:text-3xl">
              {t("aboutPage.crossBorderPresence.title")}
            </h2>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-[#6f5a63] md:text-base">
              {t("aboutPage.crossBorderPresence.description1")}
            </p>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-[#6f5a63] md:text-base">
              {t("aboutPage.crossBorderPresence.description2")}
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <div className="rounded-[1.4rem] border border-[#ead7d1] bg-white/80 p-4 shadow-[0_16px_35px_-28px_rgba(88,43,62,0.45)]">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#b17075]">
                  Türkiye
                </p>
                <p className="mt-2 text-sm leading-7 text-[#6f5a63]">
                  {t("aboutPage.crossBorderPresence.turkiyeDescription")}
                </p>
              </div>
              <div className="rounded-[1.4rem] border border-[#ead7d1] bg-white/80 p-4 shadow-[0_16px_35px_-28px_rgba(88,43,62,0.45)]">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#b17075]">
                  Japan
                </p>
                <p className="mt-2 text-sm leading-7 text-[#6f5a63]">
                  {t("aboutPage.crossBorderPresence.japanDescription")}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-between rounded-[1.8rem] bg-[linear-gradient(145deg,#8f465f,#b87a73)] p-6 text-white text-center md:text-left shadow-[0_24px_60px_-40px_rgba(83,36,54,0.58)]">
            <div className="mx-auto md:mx-0 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/14 text-2xl text-white">
              <FiCompass />
            </div>
            <div className="mt-8">
              <p className="text-sm uppercase tracking-[0.22em] text-white/70">
                {t("aboutPage.crossBorderPresence.promiseEyebrow")}
              </p>
              <h3 className="mt-3 text-2xl font-semibold leading-tight">
                {t("aboutPage.crossBorderPresence.promiseTitle")}
              </h3>
              <p className="mt-4 text-sm leading-7 text-white/85">
                {t("aboutPage.crossBorderPresence.promiseDescription")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-16 sm:px-6 lg:px-8 lg:pb-20">
        <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)]">
          <div className="overflow-hidden rounded-[2rem] bg-[linear-gradient(145deg,#8f465f,#c38b7b)] p-8 text-white text-center md:text-left shadow-[0_28px_70px_-36px_rgba(83,36,54,0.6)] md:p-10">
            <p className="text-sm uppercase tracking-[0.26em] text-white/70">
              {t("aboutPage.storyEyebrow")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold leading-tight md:text-4xl">
              {t("aboutSection.block1.title")}
            </h2>
            <p className="mt-5 max-w-2xl text-sm leading-8 text-white/85 md:text-base">
              {t("aboutSection.block1.paragraph")}
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {values.map((value, index) => {
                const Icon = valueIcons[index] || FiFeather;

                return (
                  <div
                    key={value.title}
                    className="rounded-[1.4rem] border border-white/16 bg-white/10 p-4 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 hover:border-white/40 text-center md:text-left"
                  >
                    <div className="flex items-center justify-center md:justify-start gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/12 text-xl text-white">
                        <Icon />
                      </div>
                      <h3 className="text-lg font-semibold">{value.title}</h3>
                    </div>
                    <p className="mt-3 text-sm leading-7 text-white/78">
                      {value.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid gap-6">
            <div
              className="overflow-hidden rounded-[2rem] border border-[#ecd9d1] bg-white shadow-[0_24px_60px_-38px_rgba(76,36,56,0.45)]"
              onMouseEnter={() => setIsCollectionSliderPaused(true)}
              onMouseLeave={() => setIsCollectionSliderPaused(false)}
              onFocusCapture={() => setIsCollectionSliderPaused(true)}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setIsCollectionSliderPaused(false);
                }
              }}
            >
              <div className="relative h-[260px] w-full overflow-hidden">
                {collectionSlides.map((slide, index) => (
                  <img
                    key={slide.title}
                    src={slide.image}
                    alt={slide.title}
                    className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700 ${
                      activeCollectionSlide === index
                        ? "opacity-100"
                        : "opacity-0"
                    }`}
                  />
                ))}

                <button
                  type="button"
                  onClick={() =>
                    setActiveCollectionSlide(
                      (activeCollectionSlide - 1 + collectionSlides.length) %
                        collectionSlides.length,
                    )
                  }
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 px-2 py-1 text-[#83375B] shadow-sm hover:bg-white"
                  aria-label={t("aboutPage.slider.previousSlide")}
                >
                  {"<"}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setActiveCollectionSlide(
                      (activeCollectionSlide + 1) % collectionSlides.length,
                    )
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/80 px-2 py-1 text-[#83375B] shadow-sm hover:bg-white"
                  aria-label={t("aboutPage.slider.nextSlide")}
                >
                  {">"}
                </button>
              </div>
              <div className="flex min-h-[250px] flex-col p-6 text-center md:text-left">
                <p className="text-sm uppercase tracking-[0.24em] text-[#b17075]">
                  {t("aboutPage.collectionEyebrow")}
                </p>
                <h3 className="mt-3 min-h-[40px] text-2xl font-semibold text-[#83375B]">
                  {collectionSlides[activeCollectionSlide].title}
                </h3>
                <p className="my-3 min-h-[112px] text-sm leading-7 text-[#6f5a63]">
                  {collectionSlides[activeCollectionSlide].description}
                </p>
                <div className="mt-auto flex items-center justify-center md:justify-start gap-2 pt-4">
                  {collectionSlides.map((slide, index) => (
                    <button
                      key={slide.title}
                      type="button"
                      onClick={() => setActiveCollectionSlide(index)}
                      className={`h-2.5 rounded-full transition-all ${
                        activeCollectionSlide === index
                          ? "w-7 bg-[#83375B]"
                          : "w-2.5 bg-[#d8b7ad] hover:bg-[#bf9184]"
                      }`}
                      aria-label={t("aboutPage.slider.goToSlide", {
                        number: index + 1,
                      })}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-[2rem] border border-[#f0ddd6] bg-[linear-gradient(160deg,#fff8f4,#f5ebe6)] p-6 text-center md:text-left shadow-[0_24px_60px_-42px_rgba(76,36,56,0.45)]">
              <p className="text-sm uppercase tracking-[0.24em] text-[#b17075]">
                {t("aboutPage.ctaEyebrow")}
              </p>
              <h3 className="mt-3 text-2xl font-semibold text-[#83375B]">
                {t("aboutPage.ctaTitle")}
              </h3>
              <p className="mt-3 text-sm leading-7 text-[#6f5a63]">
                {t("aboutPage.ctaDescription")}
              </p>
              <div className="mt-6 flex flex-wrap justify-center md:justify-start gap-3">
                <Link
                  to={routes.products}
                  className="inline-flex items-center gap-2 rounded-full bg-[#83375B] px-5 py-3 text-sm font-semibold tracking-[0.14em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#6d2c4d]"
                >
                  {t("aboutPage.ctaPrimary")}
                  <FiArrowRight />
                </Link>
                <Link
                  to={routes.contactUs}
                  className="rounded-full border border-[#d9bbb1] px-5 py-3 text-sm font-semibold tracking-[0.14em] text-[#83375B] transition-all duration-300 hover:border-[#83375B] hover:bg-white"
                >
                  {t("aboutPage.ctaSecondary")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
